<template>
  <div class="login-container">
    <div class="login-card">
      <h2>Iniciar sesión</h2>

      <div class="login-modes">
        <button
          type="button"
          class="mode-button"
          :class="{ active: loginMode === 'credentials' }"
          :disabled="isSubmitting"
          @click="setLoginMode('credentials')"
        >
          Email y contraseña
        </button>

        <button
          type="button"
          class="mode-button"
          :class="{ active: loginMode === 'code' }"
          :disabled="isSubmitting"
          @click="setLoginMode('code')"
        >
          Código
        </button>
      </div>

      <form @submit.prevent="handleSubmit" novalidate>
        <template v-if="!isCodeLogin">
          <div class="form-group">
            <label for="email">Email</label>
            <input
              id="email"
              v-model.trim="email"
              type="email"
              required
              autocomplete="email"
              :disabled="isSubmitting"
              placeholder="Ingresa tu email"
            >
          </div>

          <div class="form-group">
            <label for="password">Contraseña</label>
            <input
              id="password"
              v-model="password"
              type="password"
              required
              autocomplete="current-password"
              :disabled="isSubmitting"
              placeholder="Ingresa tu contraseña"
            >
          </div>
        </template>

        <template v-else>
          <div class="form-group">
            <label for="groupCode">Código</label>
            <input
              id="groupCode"
              v-model.trim="groupCode"
              type="text"
              required
              autocomplete="off"
              spellcheck="false"
              placeholder="Ej: ABC123"
              :disabled="isSubmitting"
              @input="normalizeGroupCode"
            >
          </div>
        </template>

        <div
          v-if="errorMessage"
          class="error-message"
        >
          {{ errorMessage }}
        </div>

        <p class="signup-text">
          Si aún no tienes una cuenta,
          <router-link to="/signup">
            crea una aquí
          </router-link>
        </p>

        <div class="form-group">
          <button
            type="submit"
            class="login-button"
            :disabled="isSubmitting"
          >
            {{ submitButtonText }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { db } from "../firebase";
import {
  clearTeacherCodeAccess,
  setTeacherCodeAccess,
} from "../utils/teacherCodeAccess.js";

function normalizeClassCode(value) {
  return typeof value === "string" ? value.trim().toUpperCase() : "";
}

function readTrimmedString(value) {
  return typeof value === "string" ? value.trim() : "";
}

export default {
  name: "LoginPage",
  data() {
    return {
      loginMode: "credentials",
      email: "",
      password: "",
      groupCode: "",
      errorMessage: "",
      isSubmitting: false,
    };
  },
  computed: {
    isCodeLogin() {
      return this.loginMode === "code";
    },
    submitButtonText() {
      if (this.isSubmitting) {
        return "Ingresando...";
      }

      return this.isCodeLogin ? "Entrar con código" : "Iniciar sesión";
    },
  },
  methods: {
    setLoginMode(mode) {
      if (this.isSubmitting || this.loginMode === mode) {
        return;
      }

      this.loginMode = mode;
      this.errorMessage = "";
    },

    normalizeGroupCode() {
      this.groupCode = normalizeClassCode(this.groupCode);
    },

    getErrorMessage(error) {
      if (error && typeof error.code === "string") {
        if (error.code === "permission-denied") {
          return "No tienes permisos para validar ese código.";
        }

        if (error.code === "unavailable") {
          return "El servicio no está disponible en este momento. Inténtalo de nuevo.";
        }

        if (error.code === "auth/invalid-credential") {
          return "Email o contraseña incorrectos.";
        }

        if (error.code === "auth/user-not-found") {
          return "No existe una cuenta con ese email.";
        }

        if (error.code === "auth/wrong-password") {
          return "Contraseña incorrecta.";
        }

        if (error.code === "auth/invalid-email") {
          return "Ingresa un email válido.";
        }

        if (error.code === "auth/too-many-requests") {
          return "Demasiados intentos. Inténtalo más tarde.";
        }
      }

      if (typeof error === "string" && error.trim()) {
        return error;
      }

      if (error && typeof error.message === "string" && error.message.trim()) {
        return error.message;
      }

      return "Ocurrió un error al iniciar sesión.";
    },

    async loginWithCredentials() {
      const email = this.email.trim();
      const password = this.password;

      if (!email || !password) {
        throw new Error("Ingresa tu email y contraseña.");
      }

      await this.$store.dispatch("auth/login", {
        email,
        password,
      });

      clearTeacherCodeAccess();
      await this.$router.push("/");
    },

    async loginWithGroupCode() {
      const normalizedCode = normalizeClassCode(this.groupCode);

      if (!normalizedCode) {
        throw new Error("Ingresa un código válido.");
      }

      const groupCodesRef = collection(db, "groupCodes");
      const groupCodesQuery = query(
        groupCodesRef,
        where("classCode", "==", normalizedCode),
        limit(1)
      );

      const snapshot = await getDocs(groupCodesQuery);

      if (snapshot.empty) {
        throw new Error("No encontramos un grupo con ese código.");
      }

      const groupCodeDoc = snapshot.docs[0];
      const groupCodeData = groupCodeDoc.data() || {};

      const groupId = readTrimmedString(groupCodeData.groupId);
      const groupName = readTrimmedString(groupCodeData.groupName);

      if (!groupId) {
        throw new Error("El código existe, pero no tiene un groupId válido.");
      }

      clearTeacherCodeAccess();

      setTeacherCodeAccess({
        groupId,
        classCode: normalizedCode,
        groupName,
      });

      this.email = "";
      this.password = "";
      this.groupCode = normalizedCode;

      await this.$router.push("/");
    },

    async handleSubmit() {
      if (this.isSubmitting) {
        return;
      }

      this.errorMessage = "";
      this.isSubmitting = true;

      try {
        if (this.isCodeLogin) {
          await this.loginWithGroupCode();
        } else {
          await this.loginWithCredentials();
        }
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
}

.login-card {
  width: 100%;
  max-width: 360px;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.3);
  background: white;
}

.login-card h2 {
  margin: 0 0 1rem;
  text-align: center;
}

.login-modes {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.mode-button {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #d4d4d4;
  background: #f5f5f5;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.mode-button.active {
  background: rgb(101, 1, 173);
  color: white;
  border-color: rgb(101, 1, 173);
}

.mode-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.form-group label {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.form-group input {
  padding: 0.75rem 0.75rem;
  border-radius: 5px;
  font-size: 1rem;
  border: 1px solid #d4d4d4;
}

.error-message {
  color: red;
  margin-bottom: 1rem;
  text-align: center;
}

.signup-text {
  margin-bottom: 1rem;
  text-align: center;
}

.login-button {
  width: 100%;
  background: rgb(101, 1, 173);
  color: white;
  padding: 16px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: all 0.3s;
}

.login-button:hover:not(:disabled) {
  background: rgb(0, 0, 207);
}

.login-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>